<script setup>
// NOTICE: KAPTION-INTEGRATION
// Action Pill component at the bottom of the chat list sidebar
// Provides: Privacy Mode toggle + Scheduled Messages access
// Glass-style UI matching kext KaptionActionPill.tsx

import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKaptionPrivacyMode } from 'dashboard/composables/useKaptionPrivacyMode';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { onClickOutside } from '@vueuse/core';
import KaptionScheduledMessagesModal from 'dashboard/components/widgets/conversation/KaptionScheduledMessagesModal.vue';

const props = defineProps({
  isDarkMode: {
    type: Boolean,
    default: false,
  },
  scheduledMessagesCount: {
    type: Number,
    default: 0,
  },
  scheduledMessages: {
    type: Array,
    default: () => [],
  },
  isFetchingScheduled: {
    type: Boolean,
    default: false,
  },
  isCancellingScheduled: {
    type: Boolean,
    default: false,
  },
  forceShow: {
    type: Boolean,
    default: false,
  },
  accountLevel: {
    type: Boolean,
    default: false,
  },
  searchQuery: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'cancelScheduledMessage',
  'searchScheduled',
  'refreshScheduled',
]);

const { t } = useI18n();

let isKaptionFlagEnabled = () => false;
try {
  // useKaptionFeatureFlags requires vuex, which may not be available in stories
  ({ isKaptionFlagEnabled } = useKaptionFeatureFlags());
} catch {
  // Silent fallback for Histoire/test environments
}
const { privacyEnabled, togglePrivacy } = useKaptionPrivacyMode();

const isExpanded = ref(false);
const pillRef = ref(null);
const showScheduledModal = ref(false);

const showPill = computed(
  () => props.forceShow || isKaptionFlagEnabled('KAPTION_ACTION_PILL')
);

const showPrivacy = computed(() =>
  isKaptionFlagEnabled('KAPTION_PRIVACY_MODE')
);

const expandPill = () => {
  isExpanded.value = true;
};

const handlePrivacyClick = e => {
  e?.stopPropagation();
  togglePrivacy();
};

const handleScheduledClick = e => {
  e?.stopPropagation();
  showScheduledModal.value = true;
  emit('refreshScheduled');
};

const openScheduledModal = e => {
  e?.stopPropagation();
  isExpanded.value = false;
  showScheduledModal.value = true;
  emit('refreshScheduled');
};

const closeScheduledModal = () => {
  showScheduledModal.value = false;
};

const handleCancelScheduled = payload => {
  emit('cancelScheduledMessage', payload);
};

const handleSearchScheduled = query => {
  emit('searchScheduled', query);
};

onClickOutside(pillRef, () => {
  isExpanded.value = false;
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div v-if="showPill" class="action-pill-container">
    <div ref="pillRef">
      <!-- Collapsed State -->
      <div
        v-if="!isExpanded"
        class="pill-collapsed"
        :class="isDarkMode ? 'pill-glass-dark' : 'pill-glass-light'"
        @click.stop="expandPill"
      >
        <!-- Privacy Icon - Direct toggle on click -->
        <button
          v-if="showPrivacy"
          class="pill-icon-btn"
          :class="isDarkMode ? 'hover:bg-white/10' : 'hover:bg-black/5'"
          @click="handlePrivacyClick"
        >
          <i
            class="size-5 transition-all duration-200"
            :class="[
              privacyEnabled ? 'i-lucide-eye-off' : 'i-lucide-eye',
              privacyEnabled
                ? isDarkMode
                  ? 'text-kpt-text-primary-dark'
                  : 'text-kpt-text-primary-light'
                : isDarkMode
                  ? 'text-kpt-text-muted-dark'
                  : 'text-kpt-text-muted-light',
            ]"
          />
        </button>

        <!-- Scheduled Messages Icon - Direct open modal on click -->
        <button
          class="pill-icon-btn"
          :class="isDarkMode ? 'hover:bg-white/10' : 'hover:bg-black/5'"
          @click="handleScheduledClick"
        >
          <i
            class="i-lucide-calendar-clock size-5"
            :class="
              isDarkMode
                ? 'text-kpt-text-primary-dark'
                : 'text-kpt-text-primary-light'
            "
          />
          <span
            v-if="scheduledMessagesCount > 0"
            class="absolute -top-1 -right-1 h-3.5 min-w-[14px] px-0.5 flex items-center justify-center text-[9px] font-medium rounded-full bg-kpt-emerald text-white"
          >
            {{ scheduledMessagesCount > 9 ? '9+' : scheduledMessagesCount }}
          </span>
        </button>
      </div>

      <!-- Expanded State -->
      <div
        v-else
        class="pill-expanded"
        :class="isDarkMode ? 'pill-glass-dark' : 'pill-glass-light'"
      >
        <!-- Privacy Mode Row -->
        <div
          v-if="showPrivacy"
          class="feature-row"
          :class="isDarkMode ? 'hover:bg-white/10' : 'hover:bg-black/5'"
          @click="handlePrivacyClick"
        >
          <div class="feature-info">
            <i
              class="size-4 transition-all duration-200"
              :class="[
                privacyEnabled ? 'i-lucide-eye-off' : 'i-lucide-eye',
                privacyEnabled
                  ? isDarkMode
                    ? 'text-kpt-text-primary-dark'
                    : 'text-kpt-text-primary-light'
                  : isDarkMode
                    ? 'text-kpt-text-muted-dark'
                    : 'text-kpt-text-muted-light',
              ]"
            />
            <span
              class="text-sm"
              :class="
                privacyEnabled
                  ? isDarkMode
                    ? 'text-kpt-text-primary-dark'
                    : 'text-kpt-text-primary-light'
                  : isDarkMode
                    ? 'text-kpt-text-muted-dark'
                    : 'text-kpt-text-muted-light'
              "
            >
              {{ t('CONVERSATION.KAPTION_PRIVACY_MODE.LABEL') }}
            </span>
          </div>
        </div>

        <!-- Scheduled Messages Row -->
        <div
          class="feature-row cursor-pointer group"
          :class="isDarkMode ? 'hover:bg-white/10' : 'hover:bg-black/5'"
          @click="openScheduledModal"
        >
          <div class="feature-info">
            <i
              class="i-lucide-calendar-clock size-4"
              :class="
                isDarkMode
                  ? 'text-kpt-text-primary-dark'
                  : 'text-kpt-text-primary-light'
              "
            />
            <span
              class="text-sm"
              :class="
                isDarkMode
                  ? 'text-kpt-text-primary-dark'
                  : 'text-kpt-text-primary-light'
              "
            >
              {{ t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.MODAL_TITLE') }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <span
              v-if="scheduledMessagesCount > 0"
              class="h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[11px] font-medium rounded-full bg-kpt-emerald text-white"
            >
              {{ scheduledMessagesCount }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!--
    NOTICE: KAPTION-INTEGRATION
    Keep the viewport-level modal outside Warm Paper's clipped chat-list card.
  -->
  <Teleport to="body">
    <KaptionScheduledMessagesModal
      v-model:show="showScheduledModal"
      :scheduled-messages="scheduledMessages"
      :is-fetching="isFetchingScheduled"
      :is-cancelling="isCancellingScheduled"
      :account-level="accountLevel"
      :search-query="searchQuery"
      @close="closeScheduledModal"
      @cancel="handleCancelScheduled"
      @search="handleSearchScheduled"
    />
  </Teleport>
</template>

<style scoped lang="scss">
.action-pill-container {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 39;
  pointer-events: none;
}

.pill-collapsed,
.pill-expanded {
  pointer-events: auto;
}

// Glassmorphism base
.pill-glass-dark {
  background: rgba(30, 32, 34, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.pill-glass-light {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.pill-collapsed {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem;
  border-radius: 9999px;
  cursor: pointer;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
  }
}

.pill-icon-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  transition: all 0.2s ease;
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;

  &:hover {
    transform: scale(1.1);
  }
}

.pill-expanded {
  padding: 0.5rem;
  border-radius: 1rem;
  min-width: 240px;
  max-width: 300px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.feature-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.feature-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
</style>
